import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import { meetingsQueryKey } from 'src/meetings/hooks/useScopedMeetings';
import type { MeetingScope } from 'src/meetings/scope';

type ConfirmSpeakersBody = { speakerLabels: string[] };

// `unconfirmed` names the requested voices that had no record behind them, so the adviser
// still has to say who those are.
type ConfirmedSpeakers = { confirmed: number; unconfirmed: string[] };

/**
 * Agrees with the name already shown against a speaker's voices, rather than assigning a new
 * person. Nothing is renamed and nobody is added, so unlike {@link useAssignSpeaker} there is
 * no downstream summary or document to refresh.
 */
export const useConfirmSpeakers = (scope: MeetingScope, eventId: string) => {
  const queryClient = useQueryClient();

  return useMutation<{ data: ConfirmedSpeakers }, Error, ConfirmSpeakersBody>({
    mutationFn: body =>
      api.post<ConfirmedSpeakers>(`calendar-events/${eventId}/transcript/confirm-speakers`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: meetingsQueryKey(scope) });
      queryClient.invalidateQueries({ queryKey: ['calendar-events', eventId, 'transcript'] });
    },
  });
};
